import { createAction, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { categoriesApi } from "@api";
import { RootState, setSelectedLocations } from ".";
import {
    CategoryData,
    CategoryDataByLabel,
    CategoryTours,
    HomePageProductState,
    HomePageSchema,
    ITour,
} from "@interfaces";
import { filter, find, forEach, get, isArray, keys, map, set, toLower, union } from "lodash";

export const setCachedHomePageData = createAction<{
    data: Record<string, CategoryTours>;
    cacheKey: string;
}>("homePageProduct/setCachedData");

export const setCurrentCacheKey = createAction<string>("homePageProduct/setCurrentCacheKey");

const initialState: HomePageProductState = {
    homePageProducts: {},
    cache: {},
    homePageSchema: null,
    selectedChipType: "",
    status: "idle",
    error: null,
    currentCacheKey: null,
};

// Helper functions
const generateCacheKey = (
    chipType: string,
    locations: {
        country?: { id: string } | null | undefined;
        state?: { id: string } | null | undefined;
        region?: { id: string } | null | undefined;
        locality?: { id: string } | null | undefined;
    },
) => {
    const { country, state, region, locality } = locations;
    return `${chipType}_${country?.id ?? ""}_${state?.id ?? ""}_${region?.id ?? ""}_${locality?.id ?? ""}`;
};

const buildDefaultFilters = (selectedSection: any) => {
    const defaultFilters: Record<string, Record<string, Array<number | string>>> = {};
    forEach(selectedSection?.labels, ({ label, categories }: any) => {
        if (!defaultFilters[label]) {
            defaultFilters[label] = {};
        }
        forEach(categories, ({ category_type, category_id }: any) => {
            set(
                defaultFilters,
                [label, category_type],
                union(get(defaultFilters, [label, category_type], []), [category_id]),
            );
        });
    });
    return defaultFilters;
};

const transformData = (filteredResponses: any) => {
    const transformedData: Record<string, CategoryTours> = {};
    forEach(filteredResponses, ({ label, data }: any) => {
        const tours: ITour[] = map(data, (tour: any) => ({
            ...tour,
            productId: tour.productId,
            name: tour.name,
            location: `${tour.categories.startLocationName}, ${tour.categories.countryNames[0]}`,
            durationDays: tour.durationDays,
            durationNight: tour.durationNight,
            rrp: Number(tour.rrp || 0),
            productImagePath: tour.productLargeSizeImagePath,
            productSmallToMediumSizeImagePath: tour.productSmallToMediumSizeImagePath,
            productLargeSizeImagePath: tour.productLargeSizeImagePath,
        })).sort((a, b) => a.name.localeCompare(b.name));
        transformedData[label] = {
            categoryName: label,
            tours,
        };
    });
    return transformedData;
};

async function fetchWithRetries(
    locs: any,
    filterObj: Record<string, any>,
    chip: string,
    maxItems = 10,
    pageSize = 10,
    maxPages = 4,
): Promise<CategoryDataByLabel[]> {
    let items: CategoryDataByLabel[] = [];
    let page = 0;

    while (page < maxPages) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await categoriesApi.getHomepageProductByUser(
            locs.country?.id || "",
            locs.state?.id || "",
            locs.region?.id || "",
            locs.locality?.id || "",
            filterObj,
            { "records-start": page * pageSize, "records-length": pageSize },
        );
        const batch = get(resp, "data.results", []);

        // Merge & dedupe
        const byId: Record<string, any> = {};
        [...items, ...batch].forEach((i: any) => (byId[i.productId] = i));
        items = Object.values(byId);

        // If not accommodation, we only want first page
        if (chip !== "accommodation") break;

        // Check if we have enough A’s
        const aCount = items.filter((i: any) => i.productClass === "A").length;
        if (aCount >= maxItems) break;

        page++;
    }

    // If accommodation, filter to A’s, else return full set
    return chip === "accommodation" ? items.filter((i: any) => i.productClass === "A") : items;
}

export const fetchHomePageSchema = createAsyncThunk<HomePageSchema, void, { rejectValue: string }>(
    "HomePageSchema/fetchHomePageSchema",
    async (_, thunkAPI) => {
        try {
            const res = await categoriesApi.getSchema();
            return res.data;
        } catch (error) {
            console.error(error);
            return thunkAPI.rejectWithValue("Failed to fetch categories");
        }
    },
);

export const fetchHomePageProductWithType = createAsyncThunk<
    { data: Record<string, CategoryTours>; cacheKey: string; usedCache: boolean },
    void,
    { rejectValue: string; state: RootState }
>("HomePageProductWithType/fetchHomePageProductWithType", async (_, thunkAPI) => {
    try {
        const state = thunkAPI.getState();
        const schema = state.homePageProduct.homePageSchema;
        //added replace(/s$/, "") here due to change in the backend schema
        const chipType = toLower(state.homePageProduct.selectedChipType).replace(/s$/, "");
        const selectedLocations = state.locationSearch?.selectedLocations ?? {};
        const cacheKey = generateCacheKey(chipType, selectedLocations);
        thunkAPI.dispatch(setCurrentCacheKey(cacheKey));
        const cache = state.homePageProduct.cache;
        let usedCache = false;
        if (cache && cache[cacheKey]) {
            thunkAPI.dispatch(setCachedHomePageData({ data: cache[cacheKey], cacheKey }));
            usedCache = true;
        }
        //added replace(/s$/, "") here to match the type and chip
        // TODO: check why we need to replace the s with nothing
        const selectedSection = find(
            schema?.data,
            (section: any) => toLower(section.type).replace(/s$/, "") === chipType,
        );
        const defaultFilters = buildDefaultFilters(selectedSection);

        const onlyCountrySet =
            selectedLocations.country &&
            !selectedLocations.state &&
            !selectedLocations.region &&
            !selectedLocations.locality;

        let rawResponses: { label: string; data: any[] }[] = [];

        if (onlyCountrySet && chipType) {
            const res = await categoriesApi.getHomepageProduct({
                chipType,
                selectedLocations: { country: selectedLocations.country },
            });
            const data = (res.data?.data ?? []) as CategoryData[];
            rawResponses = map(data, (item) => ({
                label: item.label,
                data: map(item.products, (p: any) => p.json),
            }));
        } else {
            rawResponses = await Promise.all(
                map(defaultFilters, async (filters, label) => {
                    const [categoryType] = keys(filters);
                    const categoryFilter = { [categoryType]: filters[categoryType] };
                    const data = await fetchWithRetries(
                        selectedLocations,
                        categoryFilter,
                        chipType,
                    );
                    return { label, data };
                }),
            );
        }
        const filteredResponses = filter(
            rawResponses,
            ({ data }: any) => isArray(data) && data.length > 0,
        );
        const transformedData = transformData(filteredResponses);
        return { data: transformedData, cacheKey, usedCache };
    } catch (err) {
        console.error("Fetch HomePage failed:", err);
        return thunkAPI.rejectWithValue("Failed to fetch products");
    }
});

const homePageProductSlice = createSlice({
    name: "homePageProduct",
    initialState,
    reducers: {
        setChipType: (state, action: PayloadAction<string>) => {
            if (state.selectedChipType === action.payload) return; // Only proceed if chip type changes
            state.selectedChipType = action.payload;
            state.homePageProducts = {}; // Clear previous data immediately
        },
        setCurrentCacheKey: (state, action: PayloadAction<string>) => {
            state.currentCacheKey = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            //Clearing cache and localStorage beacuse of the storage issue(not enough Storage).
            // Clear cache and localStorage when locations changes
            .addCase(setSelectedLocations, (state) => {
                state.cache = {};
                state.currentCacheKey = null;
            })
            .addCase(fetchHomePageSchema.pending, (state) => {
                state.status = "loading";
            })
            .addCase(
                fetchHomePageSchema.fulfilled,
                (state, action: PayloadAction<HomePageSchema>) => {
                    state.status = "succeeded";
                    state.homePageSchema = action.payload;
                },
            )
            .addCase(fetchHomePageSchema.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload ?? "An unknown error occurred";
            })
            .addCase(fetchHomePageProductWithType.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchHomePageProductWithType.fulfilled, (state, action) => {
                const { data, cacheKey, usedCache } = action.payload;
                state.cache[cacheKey] = data;
                if (!usedCache) {
                    state.homePageProducts = data;
                }
                state.status = "succeeded";
            })
            .addCase(fetchHomePageProductWithType.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload ?? "Failed to fetch products";
            })
            .addCase(setCachedHomePageData, (state, action) => {
                if (action.payload.cacheKey === state.currentCacheKey) {
                    state.homePageProducts = action.payload.data;
                    state.status = "succeeded";
                }
            });
    },
});

export const { setChipType } = homePageProductSlice.actions;
export const selectHomePageProduct = (state: RootState) => state.homePageProduct;
export default homePageProductSlice.reducer;
